import api from '../../services/api';

export const getSellersRequest = async (params) => {
  return await api.get('admins/sellers/', { params });
};

/**
 * Creates a seller and, in the same call, its company, its billing address, its
 * user account and every warehouse it was given.
 *
 * @param {object} payload - See `buildCreateSellerPayload`
 */
export const createSellerRequest = async (payload) => {
  return await api.post('admins/sellers/create/', payload);
};

/**
 * Checks whether an email is still free.
 *
 * `{ email_verification: true }` means available — the flag reads as "the
 * address passed verification", not as "the user exists".
 *
 * @param {string} email
 */
export const checkSellerEmailRequest = async (email) => {
  return await api.get(`admins/get_user_by_username/${encodeURIComponent(email)}/`);
};

/**
 * Flat list of every seller the signed-in admin manages, shaped for a select.
 *
 * Distinct from `admins/sellers/` above, which is paginated for the table.
 */
export const getAdminSellersListRequest = async () => {
  return await api.get('admins/sellers-list/');
};

/**
 * Warehouses belonging to a seller.
 *
 * Keyed by seller id, unlike `getCompanyWarehousesRequest`, which is keyed by
 * company id.
 *
 * @param {number|string} sellerId
 */
export const getSellerWarehousesRequest = async (sellerId) => {
  return await api.get(`admins/seller_warehouses/${sellerId}/`);
};

/**
 * Warehouses of the signed-in seller's own company. The endpoint resolves the
 * seller from the session, so it takes no id.
 */
export const getOwnWarehousesRequest = async () => {
  return await api.get('sellers/warehouses/');
};

export const downloadSellersRequest = async () => {
  return await api.post('admins/download_admin_sellers/');
};

export const getCompanyRequest = async (id) => {
  return await api.get(`companies/get-company/${id}/`);
};

export const getSellerRequest = async (id) => {
  return await api.get(`sellers/get-seller/${id}/`);
};

export const getCompanyWarehousesRequest = async (id) => {
  return await api.get(`warehouses/get_company_warehouses/${id}/`);
};

export const getCompanyOperatorsRequest = async (id) => {
  return await api.get(`operators/company/get-all/${id}/`);
};

export const getCompanyIntegrationsRequest = async (id) => {
  return await api.get(`companies/admin/company/${id}/setting_ecommerces/`);
};

export const changeSellerPasswordRequest = async (sellerId, payload) => {
  return await api.post(`sellers/change_password/${sellerId}/`, payload);
};

export const downloadSellerWarehousesRequest = async (sellerId) => {
  return await api.post(`admins/download_seller_warehouses/${sellerId}/`);
};

/*
 * Courier settings of a company (`SettingCourier`): whether the courier works in
 * stand-by and whether it needs pickup requests. Keyed by company id, as the
 * legacy `useCompanyDetails` called them.
 */

export const getCompanySettingCouriersRequest = async (companyId) => {
  return await api.get(`companies/admin/company/${companyId}/setting_couriers/`);
};

/**
 * @param {number|string} companyId
 * @param {string} courier
 * @param {{stand_by_enabled: boolean, requires_pickup_requests: boolean}} configuration
 */
export const createCompanySettingCourierRequest = async (companyId, courier, configuration) => {
  return await api.post(`companies/admin/company/${companyId}/setting_couriers/create/`, {
    setting_courier: { courier, configuration },
  });
};

/** The backend replaces `configuration` whole: send every flag, not only the one that changed. */
export const updateCompanySettingCourierRequest = async (companyId, settingCourierId, configuration) => {
  return await api.patch(
    `companies/admin/company/${companyId}/setting_couriers/update/${settingCourierId}/`,
    { setting_courier: { configuration } }
  );
};

export const deleteCompanySettingCourierRequest = async (companyId, settingCourierId) => {
  return await api.delete(
    `companies/admin/company/${companyId}/setting_couriers/delete/${settingCourierId}/`
  );
};

/**
 * The couriers the signed-in admin can configure for a seller: the ones it is
 * allowed and has at least one shipping zone for. `[{ id, courier }]`.
 */
export const getAvailableCouriersRequest = async () => {
  return await api.get('companies/available_couriers/');
};

/**
 * The hours a courier may collect at a warehouse. Only the fields sent are
 * written, and the answer is the whole warehouse.
 *
 * @param {number} warehouseId
 * @param {{all_day_pickup: boolean, am_pickup_from?: string, am_pickup_to?: string, pm_pickup_from?: string, pm_pickup_to?: string}} schedule
 */
export const updateWarehousePickupScheduleRequest = async (warehouseId, schedule) => {
  return await api.patch(`warehouses/update_pickup_schedule/${warehouseId}/`, schedule);
};
